import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { type CognitoAuthentication } from "@src/lib/cognito/src/lib/amplifyAuth";
/* eslint-disable no-restricted-imports */
import { getCognitoLocalStorageState } from "@src/lib/cognito/src/lib/cognitoLocalStorageInspector";
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { getFirebaseSingleton } from "@src/lib/firebase/src";
/* eslint-enable no-restricted-imports */
import type { User } from "firebase/auth";
import { useEffect, useRef } from "react";

const COGNITO_AUTH_SYNC = "useCognitoAuthSync";

/**
 * Hook that synchronizes Firebase authentication with Cognito.
 * Listens to Firebase ID token changes and automatically authenticates with Cognito
 * using the FIREBASE_TOKEN_EXCHANGE custom challenge.
 *
 * - When Firebase user signs in: authenticate with Cognito using Firebase ID token
 * - When Firebase user signs out: sign out from Cognito
 * - Errors in Cognito sync do not block Firebase authentication
 */
export function useCognitoAuthSync(): void {
  const isCognitoSyncEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC],
  });
  const isCognitoInApiCallsEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS],
  });
  const isCognitoFailClosed = useCbhFlag(CbhFeatureFlag.COGNITO_FAIL_CLOSED, {
    defaultValue: false,
  });

  const isSyncingRef = useRef(false);
  const lastSyncedUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isCognitoSyncEnabled) {
      return undefined;
    }

    const cognitoAuth = getCognitoSingleton();
    const firebaseAuth = getFirebaseSingleton();

    const unsubscribe = firebaseAuth.onIdTokenChanged(
      async (firebaseUser: User | null) => {
        if (!isDefined(firebaseUser)) {
          await handleCognitoSignOut(cognitoAuth, lastSyncedUserIdRef.current, isCognitoFailClosed);
          lastSyncedUserIdRef.current = undefined;
          return;
        }

        if (isSyncingRef.current || lastSyncedUserIdRef.current === firebaseUser.uid) {
          return;
        }

        try {
          isSyncingRef.current = true;
          logEvent(APP_V2_APP_EVENTS.COGNITO_SYNC_FLOW_STARTED, {
            firebaseUserId: firebaseUser.uid,
            isCognitoInApiCallsEnabled,
            isCognitoSyncEnabled,
            ...getCognitoLocalStorageState(),
          });
          await syncFirebaseUserWithCognito(cognitoAuth, firebaseUser, isCognitoInApiCallsEnabled);
          lastSyncedUserIdRef.current = firebaseUser.uid;
        } catch (error: unknown) {
          logError(APP_V2_APP_EVENTS.COGNITO_FIREBASE_SYNC_ERROR, {
            error,
            metadata: {
              source: COGNITO_AUTH_SYNC,
              firebaseUserId: firebaseUser.uid,
              isCognitoInApiCallsEnabled,
              cognitoFailClosed: isCognitoFailClosed,
              errorName: error instanceof Error ? error.name : undefined,
              errorMessage: error instanceof Error ? error.message : String(error),
              ...getCognitoLocalStorageState(),
            },
          });
        } finally {
          isSyncingRef.current = false;
        }
      },
      (error: unknown) => {
        logError(APP_V2_APP_EVENTS.FIREBASE_ID_TOKEN_CHANGED_ERROR, {
          error,
          metadata: { source: COGNITO_AUTH_SYNC },
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [isCognitoSyncEnabled, isCognitoInApiCallsEnabled, isCognitoFailClosed]);
}

async function handleCognitoSignOut(
  cognitoAuth: CognitoAuthentication,
  lastSyncedUserId: string | undefined,
  isCognitoFailClosed: boolean
): Promise<void> {
  if (!isDefined(lastSyncedUserId)) {
    return;
  }

  try {
    logEvent(APP_V2_APP_EVENTS.COGNITO_SIGN_OUT_INVOKED, {
      source: "useCognitoAuthSync:firebaseSignedOut",
      ...getCognitoLocalStorageState(),
    });
    await cognitoAuth.signOut();
    logEvent(APP_V2_APP_EVENTS.COGNITO_SIGN_OUT_SUCCESS, {
      source: COGNITO_AUTH_SYNC,
    });
  } catch (error: unknown) {
    logError(APP_V2_APP_EVENTS.COGNITO_SIGN_OUT_ERROR, {
      error,
      metadata: { source: COGNITO_AUTH_SYNC, cognitoFailClosed: isCognitoFailClosed },
    });
  }
}

async function syncFirebaseUserWithCognito(
  cognitoAuth: CognitoAuthentication,
  firebaseUser: User,
  isCognitoPrimaryAuth: boolean
): Promise<void> {
  const cognitoUser = await cognitoAuth.getCurrentUserInfo();
  if (isDefined(cognitoUser)) {
    // When Cognito is the primary auth provider, the login flow establishes
    // the Cognito session directly and exchanges tokens for Firebase.
    // Don't revoke the existing Cognito session — just skip the sync.
    if (isCognitoPrimaryAuth) {
      return;
    }

    const isEmailMatch =
      isDefined(firebaseUser.email) &&
      isDefined(cognitoUser.email) &&
      firebaseUser.email.trim().toLowerCase() === cognitoUser.email.trim().toLowerCase();

    const isPhoneMatch =
      isDefined(firebaseUser.phoneNumber) &&
      isDefined(cognitoUser.phoneNumber) &&
      firebaseUser.phoneNumber.trim() === cognitoUser.phoneNumber.trim();

    const isCognitoUserTheSameAsFirebaseUser = isEmailMatch || isPhoneMatch;

    if (isCognitoUserTheSameAsFirebaseUser) {
      return;
    }

    logEvent(APP_V2_APP_EVENTS.COGNITO_SYNC_SIGN_OUT_TRIGGERED, {
      firebaseEmail: firebaseUser.email,
      firebasePhone: firebaseUser.phoneNumber,
      cognitoEmail: cognitoUser.email,
      cognitoPhone: cognitoUser.phoneNumber,
      isEmailMatch,
      isPhoneMatch,
      isCognitoPrimaryAuth,
    });
    logEvent(APP_V2_APP_EVENTS.COGNITO_SIGN_OUT_INVOKED, {
      source: "useCognitoAuthSync:userMismatch",
      ...getCognitoLocalStorageState(),
    });
    // Don't emit token change, we will create
    await cognitoAuth.signOut();
  }

  const userIdentifier = firebaseUser.phoneNumber ?? firebaseUser.email;
  if (!isDefined(userIdentifier)) {
    throw new Error("User has no email or phone number");
  }

  const firebaseIdToken = await firebaseUser.getIdToken();
  await cognitoAuth.signInWithFirebaseToken({ userIdentifier, firebaseIdToken });

  logEvent(APP_V2_APP_EVENTS.COGNITO_FIREBASE_SYNC_SUCCESS, {
    source: COGNITO_AUTH_SYNC,
    userIdentifier,
  });
}
